import React, { useCallback, useEffect, useRef, useState } from 'react';

import { breakpoints } from '../theme/tokens';

const STORAGE_KEY = 'sonematic-inspector-collapsed';
const DESKTOP_QUERY = `(min-width: ${breakpoints.lg}px)`;

function isDesktop(): boolean {
  return window.matchMedia(DESKTOP_QUERY).matches;
}

function getInitialOpen(mobile: boolean): boolean {
  if (mobile) return false;
  return localStorage.getItem(STORAGE_KEY) !== 'true';
}

export interface InspectorPanelReturn {
  isOpen: boolean;
  isMobile: boolean;
  toggle: () => void;
  close: () => void;
  panelProps: {
    ref: React.RefObject<HTMLDivElement | null>;
    style: React.CSSProperties;
    onTouchStart: (e: React.TouchEvent) => void;
    onTouchMove: (e: React.TouchEvent) => void;
    onTouchEnd: (e: React.TouchEvent) => void;
  };
  edgeTabProps: {
    ref: React.RefObject<HTMLButtonElement | null>;
    onClick: () => void;
    'aria-label': string;
    onTouchStart: (e: React.TouchEvent) => void;
    onTouchMove: (e: React.TouchEvent) => void;
    onTouchEnd: (e: React.TouchEvent) => void;
  };
  backdropProps: {
    onClick: () => void;
    'aria-label': string;
  };
}

interface TouchState {
  x: number;
  y: number;
}

export function useInspectorPanel(): InspectorPanelReturn {
  const mobile = !isDesktop();
  const [isMobile, setIsMobile] = useState<boolean>(mobile);
  const [isOpen, setIsOpen] = useState<boolean>(() => getInitialOpen(mobile));

  const panelRef = useRef<HTMLDivElement | null>(null);
  const edgeTabRef = useRef<HTMLButtonElement | null>(null);
  const touchStart = useRef<TouchState | null>(null);

  // matchMedia change listener
  useEffect(() => {
    const mql = window.matchMedia(DESKTOP_QUERY);

    const handleChange = (e: MediaQueryListEvent) => {
      const nowDesktop = e.matches;
      setIsMobile(!nowDesktop);
      if (!nowDesktop) {
        // Crossed into mobile: close
        setIsOpen(false);
      } else {
        // Crossed into desktop: restore persisted preference
        setIsOpen(localStorage.getItem(STORAGE_KEY) !== 'true');
      }
    };

    mql.addEventListener('change', handleChange);
    return () => {
      mql.removeEventListener('change', handleChange);
    };
  }, []);

  // Keyboard shortcut: Cmd+. / Ctrl+.
  useEffect(() => {
    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === '.' && (e.metaKey || e.ctrlKey)) {
        setIsOpen((prev) => {
          const next = !prev;
          if (!isMobile) {
            localStorage.setItem(STORAGE_KEY, next ? 'false' : 'true');
          }
          return next;
        });
      }
    };

    window.addEventListener('keydown', handleKeydown);
    return () => {
      window.removeEventListener('keydown', handleKeydown);
    };
  }, [isMobile]);

  const toggle = useCallback(() => {
    setIsOpen((prev) => {
      const next = !prev;
      if (!isMobile) {
        localStorage.setItem(STORAGE_KEY, next ? 'false' : 'true');
      }
      return next;
    });
  }, [isMobile]);

  const close = useCallback(() => {
    setIsOpen(false);
    if (!isMobile) {
      localStorage.setItem(STORAGE_KEY, 'true');
    }
    edgeTabRef.current?.focus();
  }, [isMobile]);

  // Swipe gesture factory
  function createSwipeHandlers(direction: 'open' | 'close') {
    const onTouchStart = (e: React.TouchEvent) => {
      const touch = e.touches[0];
      touchStart.current = { x: touch.clientX, y: touch.clientY };
    };

    const onTouchMove = (_e: React.TouchEvent) => {
      // No-op during move; we read from touchStart on end
    };

    const onTouchEnd = (e: React.TouchEvent) => {
      if (!touchStart.current) return;
      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - touchStart.current.x;
      const deltaY = touch.clientY - touchStart.current.y;
      touchStart.current = null;

      // Ignore vertical swipes
      if (Math.abs(deltaY) > Math.abs(deltaX)) return;
      // Ignore small swipes
      if (Math.abs(deltaX) < 50) return;

      if (direction === 'close' && deltaX > 0) {
        setIsOpen(false);
        if (!isMobile) {
          localStorage.setItem(STORAGE_KEY, 'true');
        }
        edgeTabRef.current?.focus();
      } else if (direction === 'open' && deltaX < 0) {
        setIsOpen(true);
        if (!isMobile) {
          localStorage.setItem(STORAGE_KEY, 'false');
        }
      }
    };

    return { onTouchStart, onTouchMove, onTouchEnd };
  }

  const panelSwipeHandlers = createSwipeHandlers('close');
  const edgeTabSwipeHandlers = createSwipeHandlers('open');

  const panelStyle: React.CSSProperties = isMobile
    ? {
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: 'min(360px, 85vw)',
        transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.3s ease',
        zIndex: 9999,
      }
    : {};

  return {
    isOpen,
    isMobile,
    toggle,
    close,
    panelProps: {
      ref: panelRef,
      style: panelStyle,
      ...panelSwipeHandlers,
    },
    edgeTabProps: {
      ref: edgeTabRef,
      onClick: toggle,
      'aria-label': isOpen ? 'Hide workflow panel' : 'Show workflow panel',
      ...edgeTabSwipeHandlers,
    },
    backdropProps: {
      onClick: close,
      'aria-label': 'Close workflow panel',
    },
  };
}
